import { useReducedMotion } from 'motion/react';
import { useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';

import { YStack } from '@/components/ui/layout/layout';
import { useCallbackRef } from '@/hooks/useCallbackRef';
import { EASE_DRAWER, EASE_OUT } from '@/lib/motion-curves';
import { cn } from '@/lib/utils';

import type { RowTransition } from './use-waiting-for-you';

const SLIDE_DISTANCE_PX = 32;
const FADE_MS = 220;

type Preset = {
  readonly holdMs: number;
  readonly slideFrom: number;
  readonly slideTo: number;
  readonly slideMs: number;
  readonly slideDelayMs: number;
  readonly gapDelayMs: number;
  readonly gapMs: number;
  readonly opens: boolean;
};

const PRESETS: Record<RowTransition, Preset> = {
  completing: {
    holdMs: 3000,
    slideFrom: 0,
    slideTo: SLIDE_DISTANCE_PX,
    slideMs: 220,
    slideDelayMs: 0,
    gapDelayMs: 100,
    gapMs: 320,
    opens: false,
  },
  dismissing: {
    holdMs: 0,
    slideFrom: 0,
    slideTo: -SLIDE_DISTANCE_PX,
    slideMs: 200,
    slideDelayMs: 0,
    gapDelayMs: 70,
    gapMs: 280,
    opens: false,
  },
  returning: {
    holdMs: 0,
    slideFrom: -SLIDE_DISTANCE_PX,
    slideTo: 0,
    slideMs: 220,
    slideDelayMs: 60,
    gapDelayMs: 0,
    gapMs: 260,
    opens: true,
  },
};

const fade = (opens: boolean) => ({ opacity: opens ? [0, 1] : [1, 0] });

const useRowTransition = (
  ref: RefObject<HTMLElement | null>,
  transition: RowTransition | undefined,
  onEnd: () => void
) => {
  const reduceMotion = useReducedMotion() ?? false;
  const ended = useCallbackRef(onEnd);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!transition || !element) return;
    const preset = PRESETS[transition];

    let animations: Animation[] = [];
    let timer: ReturnType<typeof setTimeout> | undefined;
    const endWith = (last: Animation) =>
      void last.finished.then(ended, () => undefined);

    const play = () => {
      if (reduceMotion) {
        const fading = element.animate(fade(preset.opens), {
          duration: FADE_MS,
          easing: 'ease',
          fill: 'both',
        });
        animations = [fading];
        endWith(fading);
        return;
      }

      element.style.overflow = 'hidden';
      const { height } = element.getBoundingClientRect();
      const { paddingTop, paddingBottom } = getComputedStyle(element);
      const closed = { height: '0px', paddingTop: '0px', paddingBottom: '0px' };
      const open = {
        height: `${height}px`,
        paddingTop,
        paddingBottom,
      };
      const slide = element.animate(
        {
          transform: [
            `translateX(${preset.slideFrom}px)`,
            `translateX(${preset.slideTo}px)`,
          ],
          ...fade(preset.opens),
        },
        {
          delay: preset.slideDelayMs,
          duration: preset.slideMs,
          easing: EASE_OUT,
          fill: 'both',
        }
      );
      const from = preset.opens ? closed : open;
      const to = preset.opens ? open : closed;
      const gap = element.animate(
        {
          height: [from.height, to.height],
          paddingTop: [from.paddingTop, to.paddingTop],
          paddingBottom: [from.paddingBottom, to.paddingBottom],
        },
        {
          delay: preset.gapDelayMs,
          duration: preset.gapMs,
          easing: EASE_DRAWER,
          fill: 'both',
        }
      );
      animations = [slide, gap];
      endWith(gap);
    };

    if (preset.holdMs > 0) timer = setTimeout(play, preset.holdMs);
    else play();

    return () => {
      clearTimeout(timer);
      for (const animation of animations) animation.cancel();
      element.style.overflow = '';
    };
  }, [ref, transition, reduceMotion, ended]);
};

const nextOrPreviousSibling = (row: HTMLElement) =>
  (row.nextElementSibling ?? row.previousElementSibling) as HTMLElement | null;

// Spacing above a row belongs inside it, so the space closes with the row
export const TransitioningRow = ({
  transition,
  onEnd,
  findNeighbour = nextOrPreviousSibling,
  className,
  children,
}: {
  transition: RowTransition | undefined;
  onEnd: () => void;
  // The row that takes focus when this one leaves with focus inside it
  findNeighbour?: (row: HTMLElement) => HTMLElement | null | undefined;
  className: string;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useRowTransition(ref, transition, () => {
    const row = ref.current;
    const leaves = transition === 'completing' || transition === 'dismissing';
    if (leaves && row?.contains(document.activeElement))
      findNeighbour(row)
        ?.querySelector('button')
        ?.focus({ preventScroll: true });
    onEnd();
  });

  return (
    <YStack
      ref={ref}
      className={cn(
        className,
        (transition === 'completing' || transition === 'dismissing') &&
          'pointer-events-none'
      )}
    >
      {children}
    </YStack>
  );
};
